import { lazy } from 'react'
import { createBrowserRouter, Outlet } from 'react-router-dom'
import { importRemote } from 'module-federation-import-remote'
import { RedirectToNewCustomerAreaRoutes } from './components/molecules'
import { getConfig } from './helpers'
import { redirectToHome, redirectIfOldReseller } from './routes'
import moduleConfig from './config/config'
import Maintenance from './components/pages/Maintenance/Maintenance'
import { queryClient } from './service'

const { isEudora } = getConfig()

const isDevelopment = process.env.NODE_ENV === 'development'

const MFEPath = (mfe: string) =>
  isDevelopment
    ? `http://localhost:${moduleConfig[mfe as keyof typeof moduleConfig].development.port}`
    : `/${moduleConfig[mfe as keyof typeof moduleConfig].production.publicPath}`

const Vitrine = lazy(() =>
  importRemote({
    url: MFEPath('vitrine'),
    scope: 'vitrine',
    module: 'VitrineApp',
  }),
)

const Checkout = lazy(() =>
  importRemote({
    url: MFEPath('checkout'),
    scope: 'checkout',
    module: 'CheckoutApp',
  }),
)

const CustomerArea = lazy(() =>
  importRemote({
    url: MFEPath('customer_area'),
    scope: 'customer_area',
    module: 'CustomerAreaApp',
  }),
)

const Multimarcas = lazy(() =>
  importRemote({
    url: MFEPath('multimarcas'),
    scope: 'multimarcas',
    module: 'MultimarcasApp',
  }),
)

const MainLayout = lazy(() => import('./components/templates/MainLayout'))

const Catalog = lazy(() => import('./components/pages/Catalog'))

const Home = lazy(() => import('./components/pages/Home'))

const NotFound = lazy(() => import('./components/pages/NotFound'))

const NotFoundReseller = lazy(() => import('./components/pages/NotFoundReseller'))

const RedirectByExternalId = lazy(() => import('./components/pages/RedirectByExternalId'))

const RedirectToApproveSalesOnApp = lazy(
  () => import('./components/pages/RedirectToApproveSalesOnApp'),
)
const PdfCatalog = lazy(() => import('./components/pages/PdfCatalog'))

const CheckoutWrapper = () => (
  <div id={moduleConfig.checkout.production.element}>
    <Checkout />
  </div>
)

const isInMaintenance = () => process.env.REACT_APP_IS_IN_MAINTENANCE?.toString() === 'true'
const isMultiBrand = () => process.env.REACT_APP_CURRENT_BRAND?.toString() === 'MLT'

export const router = createBrowserRouter([
  {
    path: '/',
    element: isInMaintenance() ? <Maintenance /> : <Outlet />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: isMultiBrand() ? <Multimarcas /> : <Home /> },

      { path: '/login', loader: redirectToHome },
      { path: '/perfil/*', loader: redirectToHome },
      { path: '/perfil/landing-mrd', loader: redirectToHome },
      { path: '/onboarding', loader: redirectToHome },
      { path: '/criar-perfil', loader: redirectToHome },
      { path: '/escolha-estado', loader: redirectToHome },
      { path: '/conta-bloqueada', loader: redirectToHome },
      { path: '/erro-de-carregamento', loader: redirectToHome },

      {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        path: '/:reseller',
        loader: redirectIfOldReseller(queryClient),
        element: isMultiBrand() ? <Outlet /> : <MainLayout />,
        children: [
          {
            index: true,
            element: isMultiBrand() ? <Multimarcas /> : <Catalog />,
          },
          {
            path: 'catalogo',
            element: isMultiBrand() ? <Multimarcas /> : <Catalog />,
          },
        ],
      },
      {
        path: '/:reseller',
        element: <MainLayout HeaderProps={{ mainTitle: 'Voltar para catálogo' }} />,
        children: [{ path: 'area-do-consumidor/*', element: <CustomerArea /> }],
      },
      {
        path: '/:reseller',
        element: <Outlet />,
        children: [
          { path: 'menu', element: <Vitrine /> },
          { path: 'marca', element: <Vitrine /> },
          { path: 'search', element: isMultiBrand() ? <Multimarcas /> : <Vitrine /> },
          { path: 'marcas', element: <Vitrine /> },
          { path: 'categoria', element: isMultiBrand() ? <Multimarcas /> : <Vitrine /> },
          { path: 'categorias', element: <Vitrine /> },
          { path: 'meus-favoritos', element: <Vitrine /> },
          { path: 'pronta-entrega', element: isMultiBrand() ? <Multimarcas /> : <Vitrine /> },
          {
            path: 'campanha/dia-das-maes',
            element: <Vitrine />,
          },
          {
            path: 'campanha/dia-das-maes/:businessUnit/produto/:productId',
            element: <Vitrine />,
          },
          { path: 'produto/:productId', element: isMultiBrand() ? <Multimarcas /> : <Vitrine /> },
          { path: 'produtos-em-destaque', element: <Vitrine /> },
          { path: 'campanha/dia-das-maes/:businessUnit', element: <Vitrine /> },
          { path: 'pedido/*', element: <CheckoutWrapper /> },
          { path: 'carrinho/*', element: <CheckoutWrapper /> },
          { path: 'transacao/*', element: <CheckoutWrapper /> },
          { path: 'shopping-cart/*', element: <CheckoutWrapper /> },
          { path: 'aprovar-venda/:orderId', element: <RedirectToApproveSalesOnApp /> },
          {
            path: 'pdf',
            element: <PdfCatalog />,
          },
        ],
      },
      {
        path: '/catalogodigital',
        element: isMultiBrand() ? <Outlet /> : <MainLayout />,
        children: [
          { index: true, element: isMultiBrand() ? <Multimarcas /> : <Catalog /> },
          { path: ':region', element: isMultiBrand() ? <Multimarcas /> : <Catalog /> },
        ],
      },
      {
        ...(isEudora && {
          path: '/guiadigital',
          element: <MainLayout />,
          children: [
            { index: true, element: <Catalog /> },
            { path: 'guiadigital/:region', element: <Catalog /> },
          ],
        }),
      },
      {
        path: '/transacao/:transactionId',
        element: <CheckoutWrapper />,
      },
      {
        path: '/gerar-cobranca/*',
        element: <CheckoutWrapper />,
      },
      {
        path: '/nao-encontrado',
        element: <NotFoundReseller />,
      },
      {
        path: '/redirect/:externalId',
        element: <RedirectByExternalId />,
      },
      /**
       * @Deprecated - Legacy Customer Area routes
       */
      {
        path: '/area-do-consumidor/*',
        element: <RedirectToNewCustomerAreaRoutes />,
      },
    ],
  },
])
